/**
 * Pure display rules for the CRIO subject pill (SubjectIdChips).
 *
 * Split out of the component for the same reason safetyNetLane.ts is: the
 * component imports `services/api`, which cannot be loaded under the CRA test
 * runner, so anything worth pinning has to live in a dependency-free module.
 *
 * WHICH NUMBER (owner report, 2026-08-06): the pill reads the study's
 * "Subject ID#" — the number a coordinator assigns on the CRIO subject profile,
 * `studies[].studySubjectId` in the Patient API. It is NOT `crio_subject_id`,
 * which is CRIO's internal subject record number and sits in the same 7-digit
 * space as the patient id, so staff read it as "just another CRIO number". The
 * internal id stays the identity key everywhere else (lab filing, webhook
 * reverse-lookup) and appears here only in the tooltip — plus as a fallback in
 * the window between CRIO creating a subject and the site numbering it.
 */

export interface SubjectIdRow {
  /** CRIO's internal subject record id — identity key, not the number staff read. */
  crio_subject_id: string;
  crio_patient_id: string | null;
  /** The study's "Subject ID#" (site-assigned). Null until CRIO has one. */
  study_subject_id?: string | null;
  study_id: string | null;
  study_label: string | null;
  study_slug: string | null;
  status_snapshot: string | null;
  last_seen_at: string | null;
}

/**
 * What the pill reads: the study's Subject ID# when CRIO has one, otherwise
 * CRIO's internal subject number so the chip still says "this lead is a subject"
 * during the gap before a coordinator assigns it.
 */
export function displayId(r: SubjectIdRow): string {
  return r.study_subject_id || r.crio_subject_id;
}

/** Spells out every id on the row, so nothing is lost by showing only one. */
export function tooltipFor(r: SubjectIdRow): string {
  const parts = [
    r.study_subject_id
      ? `Subject ID# ${r.study_subject_id}`
      : `CRIO subject ${r.crio_subject_id} — no Subject ID# assigned in CRIO yet`,
  ];
  if (r.study_label) parts.push(r.study_label);
  if (r.status_snapshot) parts.push(r.status_snapshot);
  if (r.study_subject_id) parts.push(`CRIO subject ${r.crio_subject_id}`);
  if (r.crio_patient_id) parts.push(`CRIO patient ${r.crio_patient_id}`);
  return parts.join(' · ');
}
